import { Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ihm-pick-list',
  templateUrl: './ihm-pick-list.component.html',
  styleUrls: ['./ihm-pick-list.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IhmPickListComponent), multi: true }]
})
export class IhmPickListComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input()
  public readonly filterBy: string;

  @Input()
  public readonly objectId: number | string;

  @Input()
  public readonly propagateChangesById: boolean;

  @Input()
  public readonly source: object[];

  @Input()
  public readonly showFilter: boolean;

  // Properties Style Component
  @Input()
  public readonly disabled: boolean;

  @Input()
  public readonly sourceHeader: string;

  @Input()
  public readonly targetHeader: string;

  @Input()
  public readonly filterPlaceholder: string;

  get listSourceHeaderTitle(): string {
    return `${this.sourceHeader} (${this.listItems.get(true).length} Items)`;
  }

  get listTargetHeaderTitle(): string {
    return `${this.targetHeader} (${this.listItems.get(false).length} Items)`;
  }

  public tooltipDelay = 1000;
  public sourceInputValue = '';
  public targetInputValue = '';
  public listItems: Map<boolean, object[]> = new Map<boolean, object[]>([
    [true, []], // Source Items
    [false, []] // Target Items
  ]);

  private targetSelectedItems: object[] = [];
  private selectedItemIds: Map<boolean, (string | number)[]> = new Map<boolean, (string | number)[]>([
    [true, []], // Source Items
    [false, []] // Target Items
  ]);
  private readonly debounceTimeValue: number = 400;
  private sourceFilterChanged: Subject<string> = new Subject<string>();
  private targetFilterChanged: Subject<string> = new Subject<string>();
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.listenFilterChanges(true, this.sourceFilterChanged);
    this.listenFilterChanges(false, this.targetFilterChanged);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.disabled != null) {
      this.sourceInputValue = '';
      this.targetInputValue = '';
      this.selectedItemIds = new Map<boolean, (string | number)[]>([
        [true, []],
        [false, []]
      ]);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.unsubscribe();
  }

  applySourceFilter(): void {
    this.sourceFilterChanged.next(this.sourceInputValue);
  }

  applyTargetFilter(): void {
    this.targetFilterChanged.next(this.targetInputValue);
  }

  selectListItem(sourceItem: boolean, objectId: string | number): void {
    const itemIds: (string | number)[] = this.selectedItemIds.get(sourceItem);
    let itemsSelected: (string | number)[] = [...itemIds, objectId];

    const existsItem: boolean = itemIds.some((itemId: string | number) => itemId === objectId);
    if (existsItem) {
      itemsSelected = itemIds.filter(item => item !== objectId);
    }

    this.selectedItemIds.set(sourceItem, itemsSelected);
  }

  moveAllItems(fromSource: boolean): void {
    const targets = this.listItems.get(!fromSource);
    const sources = this.listItems.get(fromSource);

    this.listItems.set(!fromSource, targets.concat(sources));
    this.listItems.set(fromSource, []);
    this.propagateMovedItems(fromSource, sources);
  }

  moveSelectedItems(fromSource: boolean): void {
    const itemIds: (string | number)[] = this.selectedItemIds.get(fromSource);
    if (itemIds?.length) {
      itemIds.forEach(item => {
        this.selectListItem(fromSource, item);
      });

      this.moveItems(fromSource, itemIds);
    }
  }

  isSelectedItem(sourceItem: boolean, objectId: string | number): boolean {
    return this.selectedItemIds.get(sourceItem).some((item: string | number) => item === objectId);
  }

  // ControlValueAccessor methods
  writeValue(currentTargetValue: (object | string | number)[]): void {
    const targets = this.source.filter((item: object) => this.someItemMatchItemsList(currentTargetValue, item));
    this.targetSelectedItems = [...targets];
    this.listItems.set(false, targets);

    this.listItems.set(
      true,
      this.source.filter((item: object) => !this.getTargetObjectItemMatch(item))
    );
  }

  propagateChange = (value: (object | string | number)[]) => {};

  registerOnChange(fn): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn): void {}

  private getTargetObjectItemMatch(item: object): object {
    return this.targetSelectedItems.find(
      (itemTarget: object | string | number) => itemTarget === item[this.objectId] || itemTarget[this.objectId] === item[this.objectId]
    );
  }

  private someItemMatchItemsList(itemsList: (object | string | number)[], item: object): boolean {
    return (
      itemsList &&
      itemsList.some((itemTarget: object) => itemTarget === item[this.objectId] || itemTarget[this.objectId] === item[this.objectId])
    );
  }

  private hasItemFilterTerm(item: object, filterTerm: string): boolean {
    return item[this.filterBy].toLocaleLowerCase().indexOf(filterTerm.toLocaleLowerCase()) > -1;
  }

  private moveItems(fromSource: boolean, itemIds: (string | number)[]): void {
    const movedItems = this.listItems.get(fromSource).filter((item: object) => this.someItemMatchItemsList(itemIds, item));
    const slicedItems = this.listItems.get(fromSource).filter((item: object) => !this.someItemMatchItemsList(itemIds, item));

    this.listItems.set(!fromSource, this.listItems.get(!fromSource).concat(movedItems));
    this.listItems.set(fromSource, slicedItems);
    this.propagateMovedItems(fromSource, movedItems);
  }

  private propagateMovedItems(fromSource: boolean, newItems: object[]): void {
    let selectAll: object[] = [...this.targetSelectedItems.concat(newItems)];
    if (!fromSource) {
      selectAll = this.targetSelectedItems.filter((item: object) => !this.someItemMatchItemsList(newItems, item));
    }
    this.targetSelectedItems = selectAll;
    const propagateValues = this.propagateChangesById ? selectAll.map(item => item[this.objectId]) : selectAll;
    this.propagateChange(propagateValues);

    this.applySourceFilter();
    this.applyTargetFilter();
  }

  private listenFilterChanges(sourceFilter: boolean, subjectFilter: Subject<string>): void {
    subjectFilter.pipe(debounceTime(this.debounceTimeValue), takeUntil(this.ngUnsubscribe)).subscribe((searchTerm: string) => {
      if (sourceFilter) {
        this.listItems.set(
          true,
          this.source.filter((item: object) => !this.getTargetObjectItemMatch(item) && this.hasItemFilterTerm(item, searchTerm))
        );
        return;
      }

      this.listItems.set(
        false,
        this.targetSelectedItems.filter((item: object) => this.hasItemFilterTerm(item, searchTerm))
      );
    });
  }
}
